import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import type { RealtimeChannel } from '@supabase/supabase-js';

export interface Notification {
  id: string;
  from_user_id: string;
  to_user_id: string;
  collection_id: string | null;
  item_id: string | null;
  message: string | null;
  is_read: boolean;
  created_at: string;
  hidden_by_sender: boolean;
  hidden_by_receiver: boolean;
}

export interface NotificationWithDetails extends Notification {
  fromUser: {
    id: string;
    displayName: string | null;
    avatarUrl: string | null;
  };
  toUser: {
    id: string;
    displayName: string | null;
    avatarUrl: string | null;
  };
  collection: {
    id: string;
    name: string;
  } | null;
  item: {
    id: string;
    name: string;
    collectionId: string;
  } | null;
}

interface NotificationsContextValue {
  receivedNotifications: NotificationWithDetails[];
  sentNotifications: NotificationWithDetails[];
  isLoading: boolean;
  unreadCount: number;
  sendNotification: (params: {
    toUserId: string;
    collectionId?: string;
    itemId?: string;
    message?: string;
  }) => Promise<{ error: Error | null }>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  refetch: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [receivedNotifications, setReceivedNotifications] = useState<NotificationWithDetails[]>([]);
  const [sentNotifications, setSentNotifications] = useState<NotificationWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  // Ref to track current receivedNotifications for realtime handlers
  const receivedNotificationsRef = useRef<NotificationWithDetails[]>([]);
  
  // Keep ref in sync with state
  useEffect(() => {
    receivedNotificationsRef.current = receivedNotifications;
  }, [receivedNotifications]);

  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setReceivedNotifications([]);
      setSentNotifications([]);
      setUnreadCount(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const { data: notifications, error } = await supabase
        .from('notifications')
        .select('*')
        .or(`and(to_user_id.eq.${user.id},hidden_by_receiver.eq.false),and(from_user_id.eq.${user.id},hidden_by_sender.eq.false)`)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!notifications || notifications.length === 0) {
        setReceivedNotifications([]);
        setSentNotifications([]);
        setUnreadCount(0);
        setIsLoading(false);
        return;
      }

      // Get all unique user IDs, collection IDs, and item IDs
      const userIds = [...new Set([
        ...notifications.map(n => n.from_user_id),
        ...notifications.map(n => n.to_user_id),
      ])];
      const collectionIds = [...new Set(notifications.filter(n => n.collection_id).map(n => n.collection_id!))] ;
      const itemIds = [...new Set(notifications.filter(n => n.item_id).map(n => n.item_id!))];

      // Fetch profiles
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, display_name, avatar_url')
        .in('id', userIds);

      const profileMap = new Map<string, { id: string; displayName: string | null; avatarUrl: string | null }>();
      profiles?.forEach(p => {
        profileMap.set(p.id, {
          id: p.id,
          displayName: p.display_name,
          avatarUrl: p.avatar_url,
        });
      });

      // Fetch collections
      const { data: collections } = collectionIds.length > 0
        ? await supabase.from('collections').select('id, name').in('id', collectionIds)
        : { data: [] };

      const collectionMap = new Map<string, { id: string; name: string }>();
      collections?.forEach(c => {
        collectionMap.set(c.id, { id: c.id, name: c.name });
      });

      // Fetch items
      const { data: items } = itemIds.length > 0
        ? await supabase.from('collection_items').select('id, name, collection_id').in('id', itemIds)
        : { data: [] };

      const itemMap = new Map<string, { id: string; name: string; collectionId: string }>();
      items?.forEach(i => {
        itemMap.set(i.id, { id: i.id, name: i.name, collectionId: i.collection_id });
      });

      // Build notifications with details
      const notificationsWithDetails: NotificationWithDetails[] = notifications.map(n => ({
        ...n,
        hidden_by_sender: n.hidden_by_sender ?? false,
        hidden_by_receiver: n.hidden_by_receiver ?? false,
        fromUser: profileMap.get(n.from_user_id) || { id: n.from_user_id, displayName: null, avatarUrl: null },
        toUser: profileMap.get(n.to_user_id) || { id: n.to_user_id, displayName: null, avatarUrl: null },
        collection: n.collection_id ? collectionMap.get(n.collection_id) || null : null,
        item: n.item_id ? itemMap.get(n.item_id) || null : null,
      }));

      // Split into received and sent (only visible ones based on hidden flags)
      const received = notificationsWithDetails.filter(n => n.to_user_id === user.id && !n.hidden_by_receiver);
      const sent = notificationsWithDetails.filter(n => n.from_user_id === user.id && !n.hidden_by_sender);

      setReceivedNotifications(received);
      setSentNotifications(sent);
      setUnreadCount(received.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Track which notification IDs we've already processed for read status to prevent double-counting
  const processedReadNotificationsRef = useRef<Set<string>>(new Set());

  // Set up realtime subscription
  useEffect(() => {
    if (!user) return;

    fetchNotifications();

    let channel: RealtimeChannel;

    // Handle new notification INSERT - optimistic update
    const handleNewNotification = async (payload: any) => {
      const newNotif = payload.new as Notification;
      
      // Only care about notifications TO us (received) - not ones we sent
      const isReceived = newNotif.to_user_id === user.id && !newNotif.hidden_by_receiver;
      const isSent = newNotif.from_user_id === user.id && !newNotif.hidden_by_sender;
      
      // Skip if this notification isn't relevant to us
      if (!isReceived && !isSent) return;
      
      // For received notifications, immediately increment unread count for faster badge update
      if (isReceived && !newNotif.is_read) {
        setUnreadCount(prev => prev + 1);
      }
      
      // Fetch additional details for this notification
      const userIds = [newNotif.from_user_id, newNotif.to_user_id];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, display_name, avatar_url')
        .in('id', userIds);
      
      const profileMap = new Map(profiles?.map(p => [p.id, { id: p.id, displayName: p.display_name, avatarUrl: p.avatar_url }]) || []);
      
      let collection = null;
      let item = null;
      
      if (newNotif.collection_id) {
        const { data } = await supabase.from('collections').select('id, name').eq('id', newNotif.collection_id).maybeSingle();
        if (data) collection = { id: data.id, name: data.name };
      }
      
      if (newNotif.item_id) {
        const { data } = await supabase.from('collection_items').select('id, name, collection_id').eq('id', newNotif.item_id).maybeSingle();
        if (data) item = { id: data.id, name: data.name, collectionId: data.collection_id };
      }
      
      const enrichedNotif: NotificationWithDetails = {
        ...newNotif,
        hidden_by_sender: newNotif.hidden_by_sender ?? false,
        hidden_by_receiver: newNotif.hidden_by_receiver ?? false,
        fromUser: profileMap.get(newNotif.from_user_id) || { id: newNotif.from_user_id, displayName: null, avatarUrl: null },
        toUser: profileMap.get(newNotif.to_user_id) || { id: newNotif.to_user_id, displayName: null, avatarUrl: null },
        collection,
        item,
      };
      
      if (isReceived) {
        setReceivedNotifications(prev => [enrichedNotif, ...prev]);
      }
      
      if (isSent) {
        setSentNotifications(prev => [enrichedNotif, ...prev]);
      }
    };

    // Handle notification UPDATE - optimistic update for read status
    const handleNotificationUpdate = (payload: any) => {
      const updatedNotif = payload.new as Notification;
      
      // Handle is_read becoming true
      if (updatedNotif.is_read && updatedNotif.to_user_id === user.id) {
        // Check if we've already processed this to prevent double-decrement
        if (!processedReadNotificationsRef.current.has(updatedNotif.id)) {
          processedReadNotificationsRef.current.add(updatedNotif.id);
          
          // Clean up after delay to prevent memory leak
          setTimeout(() => {
            processedReadNotificationsRef.current.delete(updatedNotif.id);
          }, 5000);
          
          // Check current state via ref to determine if we should decrement
          const notification = receivedNotificationsRef.current.find(n => n.id === updatedNotif.id);
          const wasUnread = notification && !notification.is_read;
          
          // Update notifications list
          setReceivedNotifications(prev => 
            prev.map(n => n.id === updatedNotif.id ? { ...n, is_read: true } : n)
          );
          
          // Decrement count only if it was unread
          if (wasUnread) {
            setUnreadCount(count => Math.max(0, count - 1));
          }
        }
        return;
      }
      
      // Handle hidden flag changes
      if (updatedNotif.to_user_id === user.id && updatedNotif.hidden_by_receiver) {
        const notification = receivedNotificationsRef.current.find(n => n.id === updatedNotif.id);
        if (notification && !notification.is_read) {
          setUnreadCount(count => Math.max(0, count - 1));
        }
        setReceivedNotifications(prev => prev.filter(n => n.id !== updatedNotif.id));
      }
      
      if (updatedNotif.from_user_id === user.id && updatedNotif.hidden_by_sender) {
        setSentNotifications(prev => prev.filter(n => n.id !== updatedNotif.id));
      }
    };

    const setupRealtime = () => {
      channel = supabase
        .channel('notifications-context-realtime')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
          },
          handleNewNotification
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'notifications',
          },
          handleNotificationUpdate
        )
        .subscribe();
    };

    setupRealtime();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [user, fetchNotifications]);

  const sendNotification = async (params: {
    toUserId: string;
    collectionId?: string;
    itemId?: string;
    message?: string;
  }) => {
    if (!user) return { error: new Error('Not authenticated') };

    const { toUserId, collectionId, itemId, message } = params;

    const { error } = await supabase
      .from('notifications')
      .insert({
        from_user_id: user.id,
        to_user_id: toUserId,
        collection_id: collectionId || null,
        item_id: itemId || null,
        message: message || null,
      });

    if (error) {
      console.error('Error sending notification:', error);
      if (error.code === '23514') {
        toast.error('Cannot send notification to yourself');
      } else if (error.message.includes('row-level security')) {
        toast.error('This user does not accept contact requests or has blocked you');
      } else if (error.message.includes('is_user_blocked')) {
        toast.error('This user has blocked you');
      } else {
        toast.error('Failed to send notification');
      }
      return { error };
    }

    toast.success('Notification sent!');
    return { error: null };
  };

  const markAsRead = async (notificationId: string) => {
    if (!user) return;

    // Find the notification to check if it's unread
    const notification = receivedNotificationsRef.current.find(n => n.id === notificationId);
    const wasUnread = notification && !notification.is_read;

    // Optimistically update UI first
    setReceivedNotifications(prev =>
      prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
    );
    
    if (wasUnread) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }

    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .eq('id', notificationId)
      .eq('to_user_id', user.id);

    if (error) {
      console.error('Error marking notification as read:', error);
      // Revert on error
      fetchNotifications();
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;

    const unreadIds = receivedNotifications.filter(n => !n.is_read).map(n => n.id);
    if (unreadIds.length === 0) return;

    // Optimistically update UI first
    setReceivedNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);

    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true })
      .in('id', unreadIds)
      .eq('to_user_id', user.id);

    if (error) {
      console.error('Error marking all as read:', error);
      // Revert on error
      fetchNotifications();
    }
  };

  // Soft delete - sets hidden flag for current user instead of actual delete
  const deleteNotification = async (notificationId: string) => {
    if (!user) return;

    // Find the notification to determine if user is sender or receiver
    const notification = [...receivedNotifications, ...sentNotifications].find(n => n.id === notificationId);
    if (!notification) return;

    // Determine which hidden flag to set
    const isReceiver = notification.to_user_id === user.id;
    const updateData = isReceiver
      ? { hidden_by_receiver: true }
      : { hidden_by_sender: true };

    // Optimistically update UI first
    setReceivedNotifications(prev => prev.filter(n => n.id !== notificationId));
    setSentNotifications(prev => prev.filter(n => n.id !== notificationId));
    if (notification.to_user_id === user.id && !notification.is_read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }

    const { error } = await supabase
      .from('notifications')
      .update(updateData)
      .eq('id', notificationId);

    if (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification');
      // Revert on error
      fetchNotifications();
      return;
    }
    
    toast.success('Notification deleted');
  };

  return (
    <NotificationsContext.Provider
      value={{
        receivedNotifications,
        sentNotifications,
        isLoading,
        unreadCount,
        sendNotification,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        refetch: fetchNotifications,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotificationsContext() {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotificationsContext must be used within a NotificationsProvider');
  }
  return context;
}
